import { createFileRoute, getRouteApi, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { CurrentUser } from "../../lib/auth";
import { UnsavedGuard, useAttemptLeave, useUnsavedGuard, type LeaveController } from "../../components/agent/unsavedGuard";
import { useClickOutside } from "../../lib/useClickOutside";
import { DialogPanel } from "../../components/shared/DialogPanel";
import { XIcon, UsersIcon, TransferIcon } from "../../components/shared/Icons";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { backdropClose } from "../../components/shared/backdrop";

const _authedRoute = getRouteApi("/_authed");

interface Participant { id: string; name: string }
interface Client {
  id: string; name: string; slug: string; status: string; parser_used: string;
  vehicle_count: number; sem_categoria: number; manager_id: string; manager_name: string;
  participant_count?: number;
  last_updated_at: string | null; created_at: string; updated_at: string;
  source_url?: string; custom_urls?: string | null; last_error?: string | null;
  error_info?: { what: string; detail: string; code: string } | null;
  participants?: Participant[];
}

export const Route = createFileRoute("/_authed/estoque")({
  beforeLoad: ({ context }) => {
    const u = (context as { user?: CurrentUser }).user;
    if (u && u.role_name !== "Admin" && u.permissions?.viewer) {
      throw redirect({ to: "/" });
    }
  },
  validateSearch: (s: Record<string, unknown>): { erro?: boolean } => ({
    erro: s.erro === true || s.erro === "1" || s.erro === "true" ? true : undefined,
  }),
  component: EstoquePage,
});

const STATUS_LABEL: Record<string, string> = { running: "Ativo", pending: "Pendente", error: "Erro", syncing: "Atualizando…" };
const STATUS_COLOR: Record<string, string> = { running: "var(--ok)", pending: "var(--warn)", error: "var(--danger)", syncing: "var(--info)" };
const STATUS_BG: Record<string, string> = { running: "var(--ok-soft)", pending: "var(--surface-input)", error: "var(--danger-soft)", syncing: "var(--info-soft)" };
// Job travado (worker caiu no meio): syncing há mais que o job_timeout (15 min).
const SYNC_STALE_MS = 15 * 60_000;
function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const inputStyle = {
  width: "100%", padding: "9px 12px", background: "var(--surface-input)",
  border: "1px solid var(--line-bold)", borderRadius: 8, color: "var(--ink)",
  fontSize: 13, outline: "none", boxSizing: "border-box" as const,
};

const CUSTOM_URLS_PLACEHOLDER =
  "URL1=https://fonte.com/estoque.xml\nURL2=https://outra-fonte.com/feed.xml";

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR");
}

/** "Eduardo Sebastiany" → "Eduardo S." — mantém as tags do header compactas. */
function abbrevName(full: string): string {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 99,
      background: STATUS_BG[status] ?? "var(--surface-input)",
      color: STATUS_COLOR[status] ?? "var(--ink-soft)",
      border: "1px solid var(--line)",
    }}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard indisponível */ }
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, color: "var(--ink-muted)", display: "block", marginBottom: 4 }}>{label}</label>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          readOnly
          value={value}
          onFocus={e => e.currentTarget.select()}
          style={{ ...inputStyle, flex: 1, fontFamily: "monospace", fontSize: 12 }}
        />
        <button
          onClick={copy}
          style={{ flexShrink: 0, background: "var(--surface-input)", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "0 12px", fontSize: 12, cursor: "pointer" }}
        >
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
    </div>
  );
}

interface DailyLogEntry { id: string; label: string }
interface DailyLog { id: string; created_at: string; entered: DailyLogEntry[]; exited: DailyLogEntry[] }

/** Movimentação diária: até 45 registros (00:30 SP) com o que entrou/saiu do estoque.
 *  Filtro por período (7/15/30d ou personalizado) agrega client-side e abre popup. */
function DailyLogsSection({ clientId }: { clientId: string }) {
  const { data: logs, isLoading } = useQuery({
    queryKey: ["estoque-logs", clientId],
    queryFn: () => api.get<DailyLog[]>(`/estoque/${clientId}/logs`),
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [showCustom, setShowCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const isoDay = (d: Date) => d.toISOString().slice(0, 10);
  const fmtDay = (s: string) => s.split("-").reverse().join("/");
  const preset = (days: number) => {
    setShowCustom(false);
    setRange({ from: isoDay(new Date(Date.now() - (days - 1) * 864e5)), to: isoDay(new Date()) });
  };
  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    // de/até invertidos → corrige em silêncio
    setRange(customFrom <= customTo ? { from: customFrom, to: customTo } : { from: customTo, to: customFrom });
  };

  // Agrega tudo que entrou/saiu no período (os logs já estão carregados — sem chamada extra)
  const inRange = range ? (logs ?? []).filter(lg => { const d = lg.created_at.slice(0, 10); return d >= range.from && d <= range.to; }) : [];
  const periodEntered = inRange.flatMap(lg => lg.entered);
  const periodExited = inRange.flatMap(lg => lg.exited);

  const presetBtn: React.CSSProperties = { fontSize: 11, padding: "4px 10px", borderRadius: 999, cursor: "pointer", border: "1px solid var(--line-strong)", background: "var(--surface-hover)", color: "var(--ink-secondary)" };

  return (
    <div style={{ borderTop: "1px solid var(--line)", marginTop: 20, paddingTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", flex: 1 }}>Movimentação diária</div>
        {[7, 15, 30].map(d => (
          <button key={d} onClick={() => preset(d)} style={presetBtn}>{d} dias</button>
        ))}
        <button onClick={() => setShowCustom(s => !s)} style={{ ...presetBtn, ...(showCustom ? { background: "var(--brand)", color: "#fff", border: "1px solid var(--brand)" } : {}) }}>Personalizado</button>
      </div>
      {showCustom && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ fontSize: 12, padding: "4px 8px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)" }} />
          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>até</span>
          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ fontSize: 12, padding: "4px 8px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)" }} />
          <button onClick={applyCustom} disabled={!customFrom || !customTo} style={{ ...presetBtn, ...(customFrom && customTo ? { background: "var(--brand)", color: "#fff", border: "1px solid var(--brand)" } : { cursor: "default", opacity: 0.6 }) }}>Aplicar</button>
        </div>
      )}

      {/* Popup do período: um bloco só com tudo que entrou/saiu (fecha no clique fora) */}
      {range && (
        <div {...backdropClose(() => { setRange(null); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "calc(var(--z-modal) + 100)" as any, padding: 24 }}>
          <DialogPanel onClose={() => setRange(null)} label="Movimentação diária" style={{ background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 12, padding: 24, width: 440, maxHeight: "80vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", flex: 1 }}>
                Movimentação {fmtDay(range.from)} – {fmtDay(range.to)}
              </div>
              <button aria-label="Fechar" onClick={() => setRange(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--ink-muted)" }}><XIcon size={14} /></button>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <span style={{ fontSize: 12, color: "var(--ok)", fontWeight: 600 }}>+{periodEntered.length} entrou</span>
              <span style={{ fontSize: 12, color: "var(--danger)", fontWeight: 600 }}>−{periodExited.length} saiu</span>
              <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>{inRange.length} {inRange.length === 1 ? "dia registrado" : "dias registrados"}</span>
            </div>
            {periodEntered.length === 0 && periodExited.length === 0 && (
              <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Nenhuma movimentação no período.</span>
            )}
            {periodEntered.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ok)", marginBottom: 4 }}>Entraram ({periodEntered.length})</div>
                {periodEntered.map((v, i) => <div key={`${v.id}-${i}`} style={{ fontSize: 12, color: "var(--ink)" }}>{v.label}</div>)}
              </div>
            )}
            {periodExited.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--danger)", marginBottom: 4 }}>Saíram ({periodExited.length})</div>
                {periodExited.map((v, i) => <div key={`${v.id}-${i}`} style={{ fontSize: 12, color: "var(--ink)" }}>{v.label}</div>)}
              </div>
            )}
          </DialogPanel>
        </div>
      )}
      {isLoading && <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Carregando...</span>}
      {!isLoading && (!logs || logs.length === 0) && (
        <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
          Sem registros ainda. O primeiro log é gerado à meia-noite (horário de SP).
        </span>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {(logs ?? []).map(lg => {
          const open = openId === lg.id;
          return (
            <div key={lg.id} style={{ border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
              <button
                onClick={() => setOpenId(open ? null : lg.id)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--surface-hover)", border: "none", cursor: "pointer", textAlign: "left" }}
              >
                <span style={{ fontSize: 12, color: "var(--ink)", flex: 1 }}>{fmtDate(lg.created_at)}</span>
                <span style={{ fontSize: 11, color: "var(--ok)", fontWeight: 600 }}>+{lg.entered.length} entrou</span>
                <span style={{ fontSize: 11, color: "var(--danger)", fontWeight: 600 }}>−{lg.exited.length} saiu</span>
                <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{open ? "▲" : "▼"}</span>
              </button>
              {open && (
                <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10, background: "var(--surface-card)" }}>
                  {lg.entered.length === 0 && lg.exited.length === 0 && (
                    <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Nenhuma mudança neste dia.</span>
                  )}
                  {lg.entered.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ok)", marginBottom: 4 }}>Entraram ({lg.entered.length})</div>
                      {lg.entered.map(v => <div key={v.id} style={{ fontSize: 12, color: "var(--ink)" }}>{v.label}</div>)}
                    </div>
                  )}
                  {lg.exited.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--danger)", marginBottom: 4 }}>Saíram ({lg.exited.length})</div>
                      {lg.exited.map(v => <div key={v.id} style={{ fontSize: 12, color: "var(--ink)" }}>{v.label}</div>)}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

type AccessMode = null | "choose" | "rename" | "participants" | "transfer-search" | "transfer-confirm";

function DetailBody({
  clientId, canManageParticipants, canChangeOwner, canDelete, canWrite, onClose, onChanged,
}: {
  clientId: string; canManageParticipants: boolean; canChangeOwner: boolean;
  canDelete: boolean; canWrite: boolean; onClose: () => void; onChanged: () => void;
}) {
  const attemptLeave = useAttemptLeave();
  // Clique em "Atualizar agora": mantém o polling ligado até a sincronização terminar.
  const armed = useRef(false);
  const { data: c, refetch } = useQuery({
    queryKey: ["estoque-client", clientId],
    queryFn: () => api.get<Client>(`/estoque/${clientId}`),
    // Acompanha o worker: enquanto está "syncing" OU logo após o clique em
    // "Atualizar agora" (o job pode demorar a sair da fila, ou terminar antes
    // do 1º refetch — por isso não depende de ver o "syncing").
    refetchInterval: (q) => (q.state.data?.status === "syncing" || armed.current ? 2000 : false),
  });
  const syncing = c?.status === "syncing";
  // Guarda o last_updated_at do momento do clique; a sincronização terminou
  // quando ele muda (independe do relógio do browser e de ver o "syncing").
  const [baseline, setBaseline] = useState<string | null | undefined>(undefined);
  const done = baseline !== undefined && !syncing && !!c && c.last_updated_at !== baseline;
  useEffect(() => { if (done) { armed.current = false; onChanged(); } }, [done]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!syncing) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [syncing]);

  // Campos editáveis (fonte / custom URLs) + redeploy.
  const [sourceUrl, setSourceUrl] = useState("");
  const [customUrls, setCustomUrls] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [fieldsErr, setFieldsErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [redeploying, setRedeploying] = useState(false);
  const [redeployMsg, setRedeployMsg] = useState<string | null>(null);
  const [refazerStep, setRefazerStep] = useState(false);
  const [deleteStep, setDeleteStep] = useState(false);

  // Popup de projeto (renomear/participantes/proprietário) — mesmo padrão do Agente.
  const [accessMode, setAccessMode] = useState<AccessMode>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [renameErr, setRenameErr] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [participantSearch, setParticipantSearch] = useState("");
  const [transferSearch, setTransferSearch] = useState("");
  const [transferTarget, setTransferTarget] = useState<Participant | null>(null);
  const [transferring, setTransferring] = useState(false);
  const [transferErr, setTransferErr] = useState<string | null>(null);
  const participantSearchRef = useRef<HTMLDivElement>(null);
  const transferSearchRef = useRef<HTMLDivElement>(null);
  useClickOutside(participantSearchRef, participantSearch.trim().length >= 2, () => setParticipantSearch(""));
  useClickOutside(transferSearchRef, transferSearch.trim().length >= 2 && !transferTarget, () => setTransferSearch(""));

  const { data: participantResults, isError: participantSearchFailed } = useQuery({
    queryKey: ["estoque-participant-search", participantSearch],
    queryFn: () => api.get<Participant[]>(`/estoque/participants/search?q=${encodeURIComponent(participantSearch)}`),
    enabled: participantSearch.trim().length >= 2,
  });
  const { data: transferResults, isError: transferSearchFailed } = useQuery({
    queryKey: ["estoque-transfer-search", transferSearch],
    queryFn: () => api.get<Participant[]>(`/estoque/participants/search?q=${encodeURIComponent(transferSearch)}`),
    enabled: transferSearch.trim().length >= 2,
  });

  useEffect(() => {
    if (!c) return;
    setSourceUrl(c.source_url ?? "");
    setCustomUrls(c.custom_urls ?? "");
    setUseCustom(!!(c.custom_urls && c.custom_urls.trim()));
    setRenameValue(c.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.id]);

  // Compara com o que realmente seria enviado, não com o estado do checkbox —
  // marcar "Custom URLs" sem digitar nada não deve acender o Salvar (o payload
  // seria idêntico ao atual); desmarcar quando já havia custom_urls salvo deve
  // acender (precisa limpar o campo no servidor).
  const nextCustomUrls = useCustom ? (customUrls || null) : null;
  const currentCustomUrls = c?.custom_urls ?? null;
  const fieldsDirty = !!c && (sourceUrl !== (c.source_url ?? "") || nextCustomUrls !== currentCustomUrls);

  async function saveFields() {
    if (!c) return;
    setFieldsErr(null); setSaving(true);
    try {
      await api.patch(`/estoque/${clientId}`, { source_url: sourceUrl, custom_urls: nextCustomUrls });
      await refetch(); onChanged();
    } catch (e) {
      const msg = e instanceof ApiError ? String((e.payload as any)?.detail ?? "Erro ao salvar") : "Erro ao salvar";
      setFieldsErr(msg);
      throw e; // deixa o UnsavedGuard mostrar o estado de erro
    } finally { setSaving(false); }
  }

  // useUnsavedGuard chama useEffect por baixo — tem que rodar em toda
  // renderização, antes do "if (!c) return null" abaixo (Rules of Hooks:
  // hooks não podem ficar depois de um early return condicional).
  useUnsavedGuard(canWrite && fieldsDirty, saveFields);

  if (!c) return null;

  async function redeploy(refazerFotos = false) {
    setRedeployMsg(null); setRedeploying(true); setRefazerStep(false);
    try {
      const r = await api.post<{ fotos_apagadas: number }>(`/estoque/${clientId}/redeploy`, refazerFotos ? { refazer_fotos: true } : {});
      setBaseline(c!.last_updated_at); armed.current = true;
      setRedeployMsg(refazerFotos ? `${r.fotos_apagadas} foto(s) apagada(s) · na fila…` : "Na fila…");
      refetch();
    } catch (e) {
      const msg = e instanceof ApiError ? String((e.payload as any)?.detail ?? "Falha ao enfileirar") : "Falha ao enfileirar";
      setRedeployMsg(msg);
    } finally { setRedeploying(false); }
  }

  function closeAccess() {
    setAccessMode(null); setConfirmRemoveId(null);
    setParticipantSearch(""); setTransferSearch(""); setTransferTarget(null);
    setRenameValue(c?.name ?? ""); setRenameErr(null);
  }
  async function doRename() {
    const v = renameValue.trim();
    if (!v) return;
    setRenaming(true); setRenameErr(null);
    try {
      await api.patch(`/estoque/${clientId}`, { name: v });
      await refetch(); onChanged();
      closeAccess();
    } catch (e) {
      setRenameErr(e instanceof ApiError ? String((e.payload as any)?.detail ?? "Erro ao renomear") : "Erro ao renomear");
    } finally { setRenaming(false); }
  }
  async function addParticipant(userId: string) {
    await api.post(`/estoque/${clientId}/participants`, { user_id: userId });
    setParticipantSearch("");
    await refetch(); onChanged();
  }
  async function removeParticipant(userId: string) {
    await api.delete(`/estoque/${clientId}/participants/${userId}`);
    setConfirmRemoveId(null);
    await refetch(); onChanged();
  }
  async function doTransfer() {
    if (!transferTarget) return;
    setTransferring(true); setTransferErr(null);
    try {
      await api.patch(`/estoque/${clientId}/transfer-owner`, { new_manager_id: transferTarget.id });
      await refetch(); onChanged();
      closeAccess();
    } catch (e) {
      setTransferErr(e instanceof ApiError ? String((e.payload as any)?.detail ?? "Erro ao transferir. Tente novamente.") : "Erro ao transferir. Tente novamente.");
    } finally { setTransferring(false); }
  }
  async function doDelete() {
    await api.delete(`/estoque/${clientId}`);
    onChanged(); onClose();
  }

  const participants = c.participants ?? [];
  const canOpenAccess = canWrite || canManageParticipants || canChangeOwner;
  const externalUrl = `https://valquiria.revendai.com/estoque-public/${c.slug}/api/data`;
  const internalUrl = `http://carmillion-api:8000/estoque-public/${c.slug}/api/data`;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any, padding: 24 }}
      {...backdropClose(() => { attemptLeave(onClose); }, { guarded: true })}
    >
      <DialogPanel onClose={() => attemptLeave(onClose)} label={c.name} style={{ background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 12, padding: 28, width: 460, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flexWrap: "wrap" }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 2 }}>{c.name}</div>
            {canOpenAccess && (
              <button
                onClick={() => setAccessMode("choose")}
                title="Gerenciar projeto"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, border: "1px solid var(--line-strong)", borderRadius: 6, background: "var(--surface-hover)", color: "var(--ink-secondary)", cursor: "pointer", flexShrink: 0 }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              </button>
            )}
            {/* Tags: dono (vermelho) + participantes (amarelo) — mesmo padrão do Agente */}
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--brand-soft)", color: "var(--danger)", border: "1px solid var(--danger-line)", flexShrink: 0 }}>
              {abbrevName(c.manager_name)}
            </span>
            {participants.map(p => (
              <span key={p.id} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--warn-soft)", color: "var(--warn)", border: "1px solid var(--warn-line)", flexShrink: 0 }}>
                {abbrevName(p.name)}
              </span>
            ))}
          </div>
          <StatusBadge status={c.status} />
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-muted)", marginBottom: 18 }}>
          {c.vehicle_count} veículo(s) · {c.sem_categoria} sem categoria · Gestor: {c.manager_name}
        </div>
        {c.status === "error" && c.error_info && (
          <div style={{ fontSize: 12, background: "var(--danger-soft)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 12px", marginBottom: 16, display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: "var(--danger)", fontWeight: 600 }}>O que houve: <span style={{ fontWeight: 400 }}>{c.error_info.what}</span></div>
            <div style={{ color: "var(--ink-mid)" }}>Detalhe técnico: <span style={{ fontFamily: "var(--font-mono)", wordBreak: "break-word" }}>{c.error_info.detail}</span></div>
            <div style={{ color: "var(--ink-muted)" }}>Código: <span style={{ fontFamily: "var(--font-mono)" }}>{c.error_info.code}</span></div>
          </div>
        )}

        {/* Campos editáveis: checkbox + URL da fonte / Custom URLs */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ink-muted)", marginBottom: 8, cursor: canWrite ? "pointer" : "default" }}>
          <input
            type="checkbox"
            checked={useCustom}
            disabled={!canWrite}
            onChange={e => setUseCustom(e.target.checked)}
            style={{ accentColor: "var(--brand)", width: 14, height: 14 }}
          />
          Usar Custom URLs (avançado)
        </label>

        {!useCustom ? (
          <>
            <label style={{ fontSize: 12, color: "var(--ink-muted)", display: "block", marginBottom: 4 }}>URL da fonte</label>
            <input style={inputStyle} value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} disabled={!canWrite} placeholder="https://..." />
          </>
        ) : (
          <>
            <label style={{ fontSize: 12, color: "var(--ink-muted)", display: "block", marginBottom: 4 }}>Custom URLs (uma por linha)</label>
            <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={customUrls} onChange={e => setCustomUrls(e.target.value)} disabled={!canWrite} placeholder={CUSTOM_URLS_PLACEHOLDER} />
          </>
        )}
        {fieldsErr && <span style={{ fontSize: 12, color: "var(--danger)", marginTop: 8, display: "block" }}>{fieldsErr}</span>}

        {/* URLs de consulta + redeploy manual */}
        <div style={{ borderTop: "1px solid var(--line)", marginTop: 20, paddingTop: 16 }}>
          <CopyField label="URL externa (pública)" value={externalUrl} />
          <CopyField label="URL interna" value={internalUrl} />

          {canWrite && (
            <div>
              {(() => {
                const elapsed = syncing ? now - new Date(c.updated_at).getTime() : 0;
                const stale = syncing && elapsed > SYNC_STALE_MS;
                const busy = redeploying || (syncing && !stale);
                const accent = syncing ? "var(--info)" : "var(--brand)";
                const msg = done
                  ? (c.status === "error" ? "Falhou — veja o erro acima." : `Concluído às ${fmtDate(c.last_updated_at)} · ${c.vehicle_count} veículo(s)`)
                  : redeployMsg;
                return (
                  <>
                    <button
                      onClick={() => redeploy()}
                      disabled={busy}
                      style={{ background: "transparent", color: accent, border: `1px solid ${accent}`, borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: busy ? 0.7 : 1 }}
                    >
                      {redeploying ? "Enfileirando..." : syncing ? `Atualizando… ${fmtElapsed(elapsed)}` : "Atualizar agora"}
                    </button>
                    {/* Apaga as fotos do cliente no R2 e re-scrape: fotos ficam 404 até o job acabar. */}
                    <button
                      onClick={() => (refazerStep ? redeploy(true) : setRefazerStep(true))}
                      onBlur={() => setRefazerStep(false)}
                      disabled={busy}
                      title="Apaga as fotos deste cliente no armazenamento e baixa tudo de novo. As fotos ficam indisponíveis até a atualização terminar."
                      style={{ marginLeft: 8, background: "transparent", color: refazerStep ? "var(--warn)" : "var(--ink-muted)", border: `1px solid ${refazerStep ? "var(--warn)" : "var(--line)"}`, borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: busy ? 0.7 : 1 }}
                    >
                      {refazerStep ? "Confirmar: apagar e baixar de novo" : "Refazer fotos"}
                    </button>
                    {stale && <span style={{ fontSize: 12, color: "var(--warn)", marginLeft: 10 }}>Rodando há mais de 15 min — pode ter travado. Pode tentar de novo.</span>}
                    {!syncing && msg && <span style={{ fontSize: 12, color: "var(--ink-muted)", marginLeft: 10 }}>{msg}</span>}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <DailyLogsSection clientId={clientId} />

        {/* Rodapé: Salvar (esquerda) + Excluir (direita) */}
        <div style={{ borderTop: "1px solid var(--line)", marginTop: 20, paddingTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span>
              {canWrite && fieldsDirty && (
                <button onClick={() => { saveFields().catch(() => {}); }} disabled={saving} style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
                  {saving ? "Salvando..." : "Salvar alterações"}
                </button>
              )}
            </span>
            {canDelete && !deleteStep && (
              <button onClick={() => setDeleteStep(true)} style={{ background: "transparent", color: "var(--danger)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}>
                Excluir cliente
              </button>
            )}
          </div>
          {canDelete && deleteStep && (
            <ConfirmDialog
              title={`Excluir ${c.name}?`}
              message="Remove a API, participantes e histórico. As fotos expiram sozinhas em até 10 dias."
              confirmLabel="Excluir"
              danger
              onConfirm={doDelete}
              onCancel={() => setDeleteStep(false)}
            />
          )}
        </div>
      </DialogPanel>

      {/* Popup: renomear / gerenciar participantes / proprietário — mesmo padrão do Agente */}
      {accessMode !== null && (
        <div {...backdropClose(() => { closeAccess(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "calc(var(--z-modal) + 100)" as any }}>
          <DialogPanel onClose={() => closeAccess()} label={accessMode === "choose" ? "Gerenciar projeto" : accessMode === "rename" ? "Renomear projeto" : accessMode === "participants" ? "Adicionar participantes" : accessMode === "transfer-search" ? "Trocar proprietário" : "Confirmar transferência"} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 380, display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>
                {accessMode === "choose" ? "Gerenciar projeto" :
                 accessMode === "rename" ? "Renomear projeto" :
                 accessMode === "participants" ? "Adicionar participantes" :
                 accessMode === "transfer-search" ? "Trocar proprietário" :
                 "Confirmar transferência"}
              </span>
              <button onClick={closeAccess} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
            </div>

            {/* Choose screen */}
            {accessMode === "choose" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {canWrite && (
                  <button
                    onClick={() => setAccessMode("rename")}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, color: "var(--ink)", fontSize: 13, cursor: "pointer", textAlign: "left", width: "100%" }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--brand)")}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--line)")}
                  >
                    <span style={{ fontSize: 18 }}>✎</span>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Renomear projeto</div>
                      <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Altera o nome deste cliente</div>
                    </div>
                  </button>
                )}
                {canManageParticipants && (
                  <button
                    onClick={() => setAccessMode("participants")}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, color: "var(--ink)", fontSize: 13, cursor: "pointer", textAlign: "left", width: "100%" }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--brand)")}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--line)")}
                  >
                    <UsersIcon size={18} />
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Adicionar participantes</div>
                      <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Dá acesso a outros gestores neste cliente</div>
                    </div>
                  </button>
                )}
                {canChangeOwner && (
                  <button
                    onClick={() => setAccessMode("transfer-search")}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, color: "var(--ink)", fontSize: 13, cursor: "pointer", textAlign: "left", width: "100%" }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--brand)")}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--line)")}
                  >
                    <TransferIcon size={18} />
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Trocar proprietário</div>
                      <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Transfere a propriedade deste cliente para outro gestor</div>
                    </div>
                  </button>
                )}
              </div>
            )}

            {/* Rename screen */}
            {accessMode === "rename" && (
              <>
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder="Nome do projeto"
                  style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
                />
                {renameErr && <span style={{ fontSize: 12, color: "var(--danger)" }}>{renameErr}</span>}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button onClick={() => setAccessMode("choose")} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Voltar</button>
                  <button
                    onClick={doRename}
                    disabled={!renameValue.trim() || renaming}
                    style={{ padding: "7px 16px", background: renameValue.trim() ? "var(--brand)" : "var(--line)", border: "none", borderRadius: 8, color: renameValue.trim() ? "#fff" : "var(--ink-dim)", fontSize: 13, fontWeight: 600, cursor: renameValue.trim() ? "pointer" : "default", opacity: renaming ? 0.6 : 1 }}
                  >
                    {renaming ? "Salvando..." : "Salvar"}
                  </button>
                </div>
              </>
            )}

            {/* Participants screen */}
            {accessMode === "participants" && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", borderRadius: 8, background: "var(--danger-soft)" }}>
                    <span style={{ fontSize: 13, color: "var(--danger)" }}>{c.manager_name}</span>
                    <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>owner</span>
                  </div>
                  {participants.map(p => (
                    <div key={p.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", borderRadius: 8, background: "var(--warn-soft)" }}>
                        <span style={{ fontSize: 13, color: "var(--warn)" }}>{p.name}</span>
                        <button onClick={() => setConfirmRemoveId(confirmRemoveId === p.id ? null : p.id)} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
                      </div>
                      {confirmRemoveId === p.id && (
                        <div style={{ padding: "8px 10px", background: "var(--surface-card)", borderRadius: 8, border: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 8 }}>
                          <span style={{ fontSize: 12, color: "var(--ink-mid)" }}>Remover <strong style={{ color: "var(--ink)" }}>{p.name}</strong> dos participantes?</span>
                          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => setConfirmRemoveId(null)} style={{ padding: "4px 12px", background: "var(--line)", border: "none", borderRadius: 6, color: "var(--ink-secondary)", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
                            <button onClick={() => removeParticipant(p.id)} style={{ padding: "4px 12px", background: "var(--danger-soft)", border: "none", borderRadius: 6, color: "var(--danger)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Remover</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ borderTop: "1px solid var(--line)" }} />

                <div ref={participantSearchRef} style={{ position: "relative" }}>
                  <input
                    value={participantSearch}
                    onChange={(e) => setParticipantSearch(e.target.value)}
                    placeholder="Buscar gestor por nome..."
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
                  />
                  {participantSearch.trim().length >= 2 && (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", zIndex: 10 }}>
                      {participantSearchFailed ? (
                        <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--danger)" }}>Erro ao buscar. Tente novamente.</div>
                      ) : !participantResults ? (
                        <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--ink-muted)" }}>Buscando...</div>
                      ) : participantResults.filter(u => u.id !== c.manager_id && !participants.some(p => p.id === u.id)).length === 0 ? (
                        <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--ink-muted)" }}>Nenhum usuário encontrado.</div>
                      ) : (
                        participantResults
                          .filter(u => u.id !== c.manager_id && !participants.some(p => p.id === u.id))
                          .map(u => (
                            <button key={u.id} onClick={() => addParticipant(u.id)}
                              style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", color: "var(--ink)", fontSize: 13, cursor: "pointer" }}
                              onMouseEnter={e => (e.currentTarget.style.background = "var(--line)")}
                              onMouseLeave={e => (e.currentTarget.style.background = "none")}
                            >{u.name}</button>
                          ))
                      )}
                    </div>
                  )}
                </div>

                {(canWrite || canChangeOwner) && (
                  <button onClick={() => { setAccessMode("choose"); setConfirmRemoveId(null); setParticipantSearch(""); }} style={{ alignSelf: "flex-start", background: "none", border: "none", color: "var(--ink-muted)", fontSize: 12, cursor: "pointer", padding: 0 }}>
                    ← Voltar
                  </button>
                )}
              </>
            )}

            {/* Transfer search screen */}
            {accessMode === "transfer-search" && (
              <>
                <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.5 }}>
                  Proprietário atual: <strong style={{ color: "var(--danger)" }}>{c.manager_name}</strong>
                </div>
                <div ref={transferSearchRef} style={{ position: "relative" }}>
                  <input
                    autoFocus
                    value={transferSearch}
                    onChange={(e) => { setTransferSearch(e.target.value); setTransferTarget(null); }}
                    placeholder="Buscar novo proprietário..."
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
                  />
                  {transferSearch.trim().length >= 2 && !transferTarget && (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", zIndex: 10 }}>
                      {transferSearchFailed ? (
                        <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--danger)" }}>Erro ao buscar. Tente novamente.</div>
                      ) : !transferResults ? (
                        <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--ink-muted)" }}>Buscando...</div>
                      ) : transferResults.filter((u) => u.id !== c.manager_id).length === 0 ? (
                        <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--ink-muted)" }}>Nenhum usuário encontrado.</div>
                      ) : (
                        transferResults.filter((u) => u.id !== c.manager_id).map((u) => (
                          <button key={u.id} onClick={() => { setTransferTarget(u); setTransferSearch(u.name); }}
                            style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", color: "var(--ink)", fontSize: 13, cursor: "pointer" }}
                            onMouseEnter={e => (e.currentTarget.style.background = "var(--line)")}
                            onMouseLeave={e => (e.currentTarget.style.background = "none")}
                          >{u.name}</button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  {(canWrite || canManageParticipants) ? (
                    <button onClick={() => { setAccessMode("choose"); setTransferSearch(""); setTransferTarget(null); }} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Voltar</button>
                  ) : (
                    <button onClick={closeAccess} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
                  )}
                  <button
                    onClick={() => setAccessMode("transfer-confirm")}
                    disabled={!transferTarget}
                    style={{ padding: "7px 16px", background: transferTarget ? "var(--brand)" : "var(--line)", border: "none", borderRadius: 8, color: transferTarget ? "#fff" : "var(--ink-dim)", fontSize: 13, fontWeight: 600, cursor: transferTarget ? "pointer" : "default" }}
                  >Continuar</button>
                </div>
              </>
            )}

            {/* Transfer confirm screen */}
            {accessMode === "transfer-confirm" && transferTarget && (
              <>
                <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.6 }}>
                  O proprietário do cliente <strong style={{ color: "var(--ink)" }}>{c.name}</strong> será trocado de{" "}
                  <strong style={{ color: "var(--danger)" }}>{c.manager_name}</strong> para{" "}
                  <strong style={{ color: "var(--ok)" }}>{transferTarget.name}</strong>.
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button onClick={() => setAccessMode("transfer-search")} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Voltar</button>
                  <button
                    onClick={doTransfer}
                    disabled={transferring}
                    style={{ padding: "7px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: transferring ? 0.6 : 1 }}
                  >
                    {transferring ? "Transferindo..." : "Confirmar troca"}
                  </button>
                </div>
                {transferErr && <span style={{ fontSize: 12, color: "var(--danger)" }}>{transferErr}</span>}
              </>
            )}

          </DialogPanel>
        </div>
      )}
    </div>
  );
}

function DetailModal(props: {
  clientId: string; canManageParticipants: boolean; canChangeOwner: boolean;
  canDelete: boolean; canWrite: boolean; onClose: () => void; onChanged: () => void;
}) {
  const controllerRef = useRef<LeaveController>({ attemptLeave: (run) => run() });
  return (
    <UnsavedGuard controllerRef={controllerRef}>
      <DetailBody {...props} onClose={() => controllerRef.current.attemptLeave(props.onClose)} />
    </UnsavedGuard>
  );
}

// Cadastro global tipo→URL de foto, aplicado no scrape aos veículos que vieram
// sem nenhuma foto na carga. Só Admin abre (o backend também exige Admin).
function FotosPadraoModal({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<{ tipo: string; url: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["estoque-fotos-padrao"],
    queryFn: () => api.get<Record<string, string>>("/estoque/fotos-padrao"),
  });

  useEffect(() => {
    if (data) setRows(Object.entries(data).map(([tipo, url]) => ({ tipo, url })));
  }, [data]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const mapa: Record<string, string> = {};
      for (const r of rows) {
        if (r.tipo.trim() && r.url.trim()) mapa[r.tipo.trim()] = r.url.trim();
      }
      await api.put("/estoque/fotos-padrao", mapa);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? String((e.payload as any)?.detail ?? "Erro ao salvar.") : "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  }

  const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: "var(--ink-muted)" };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}
      {...backdropClose(() => { onClose(); })}
    >
      <DialogPanel onClose={onClose} label="Fotos padrão por tipo" style={{ background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 12, padding: 28, width: 620, maxHeight: "80vh", overflow: "auto" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Fotos padrão por tipo</div>
        <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 6, marginBottom: 18, lineHeight: 1.5 }}>
          Quando um veículo vem sem nenhuma foto na carga, o sistema usa a URL cadastrada aqui para o tipo dele.
          Vale para todos os clientes de estoque. Passa a valer no próximo scrape (ou ao atualizar o cliente).
        </div>

        {isLoading && <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando...</span>}

        {!isLoading && rows.length === 0 && (
          <div style={{ fontSize: 13, color: "var(--ink-muted)", marginBottom: 14 }}>Nenhum tipo cadastrado.</div>
        )}

        {rows.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "150px 1fr 28px", gap: 8, alignItems: "center", marginBottom: 14 }}>
            <span style={labelStyle}>TIPO</span>
            <span style={labelStyle}>URL DA FOTO</span>
            <span />
            {rows.map((r, i) => (
              <Fragment key={i}>
                <input
                  style={inputStyle}
                  placeholder="carro"
                  value={r.tipo}
                  onChange={e => setRows(rows.map((x, j) => (j === i ? { ...x, tipo: e.target.value } : x)))}
                />
                <input
                  style={inputStyle}
                  placeholder="https://..."
                  value={r.url}
                  onChange={e => setRows(rows.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                />
                <button
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}
                  title="Remover"
                  style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", padding: 4, display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <XIcon />
                </button>
              </Fragment>
            ))}
          </div>
        )}

        <button
          onClick={() => setRows([...rows, { tipo: "", url: "" }])}
          style={{ background: "transparent", color: "var(--brand)", border: "1px dashed var(--line-bold)", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}
        >
          + Adicionar tipo
        </button>

        {error && <span style={{ fontSize: 12, color: "var(--danger)", marginTop: 12, display: "block" }}>{error}</span>}

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.5 : 1 }}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
          <button onClick={onClose} style={{ flex: 1, background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>
            Cancelar
          </button>
        </div>
      </DialogPanel>
    </div>
  );
}

function EstoquePage() {
  const qc = useQueryClient();
  const routeCtx = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const u = routeCtx.user;
  const isAdmin = u?.role_name === "Admin";
  const canCreate = isAdmin || !!u?.permissions?.estoque_criar;
  const canDelete = isAdmin || !!u?.permissions?.estoque_excluir_cliente;
  const canManageParticipants = isAdmin || !!u?.permissions?.estoque_participantes;
  const canChangeOwner = isAdmin || !!u?.permissions?.estoque_alterar_proprietario;

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const createBusy = useRef(false);
  const [newSource, setNewSource] = useState("");
  const [newCustom, setNewCustom] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [managerFilter, setManagerFilter] = useState("");
  const { erro: erroFilter } = Route.useSearch();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [fotosPadrao, setFotosPadrao] = useState(false);
  const [redeployAllMsg, setRedeployAllMsg] = useState<string | null>(null);

  const { data: clients = [], isLoading, error } = useQuery({
    queryKey: ["estoque-clients"],
    queryFn: () => api.get<Client[]>("/estoque"),
  });

  async function handleCreate() {
    // Ref (não state): dois Enter seguidos no mesmo render criavam 2 clientes.
    if (!newName.trim() || createBusy.current) return;
    createBusy.current = true;
    setCreateError(null);
    try {
      const c = await api.post<Client>("/estoque", {
        name: newName.trim(), source_url: newSource.trim() || null, custom_urls: newCustom.trim() || null,
      });
      setCreating(false); setNewName(""); setNewSource(""); setNewCustom("");
      await qc.invalidateQueries({ queryKey: ["estoque-clients"] });
      setSelectedId(c.id);
    } catch (e) {
      setCreateError(e instanceof ApiError ? String((e.payload as any)?.detail ?? "Erro ao criar cliente.") : "Erro ao criar cliente.");
    } finally {
      createBusy.current = false;
    }
  }

  const q = search.toLowerCase();
  const managers = [...new Map(clients.map(c => [c.manager_id, c.manager_name])).entries()].sort((x, y) => x[1].localeCompare(y[1]));
  const filtered = clients.filter(c =>
    (!erroFilter || c.status === "error") &&
    (!managerFilter || c.manager_id === managerFilter) &&
    (!q || c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q) || c.manager_name.toLowerCase().includes(q)
      || (q.startsWith("est-") && !!c.last_error?.toLowerCase().includes(q))));  // cola o EST-xxxxxx que o cliente mandou

  const thStyle: React.CSSProperties = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--ink-muted)", padding: "8px 12px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" };
  const tdStyle: React.CSSProperties = { fontSize: 13, color: "var(--ink)", padding: "10px 12px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Modal criar */}
      {creating && (
        <div
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}
          {...backdropClose(() => { setCreating(false); setCreateError(null); })}
        >
          <DialogPanel onClose={() => { setCreating(false); setCreateError(null); }} label="Novo cliente de estoque" style={{ background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 12, padding: 28, width: 420 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", marginBottom: 16 }}>Novo cliente de estoque</div>
            <input autoFocus style={inputStyle} placeholder="Nome (ex: Loja do Zé)" value={newName} onChange={e => setNewName(e.target.value)} />
            <input style={{ ...inputStyle, marginTop: 10 }} placeholder="URL da fonte (https://...)" value={newSource} onChange={e => setNewSource(e.target.value)} />
            <textarea style={{ ...inputStyle, marginTop: 10, minHeight: 56, resize: "vertical" }} placeholder={CUSTOM_URLS_PLACEHOLDER} value={newCustom} onChange={e => setNewCustom(e.target.value)} />
            {createError && <span style={{ fontSize: 12, color: "var(--danger)", marginTop: 8, display: "block" }}>{createError}</span>}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={handleCreate} disabled={!newName.trim()} style={{ flex: 1, background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: !newName.trim() ? 0.5 : 1 }}>Criar</button>
              <button onClick={() => { setCreating(false); setCreateError(null); }} style={{ flex: 1, background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Modal fotos padrão (Admin) */}
      {fotosPadrao && <FotosPadraoModal onClose={() => setFotosPadrao(false)} />}

      {/* Modal detalhe */}
      {selectedId && (
        <DetailModal
          clientId={selectedId}
          canManageParticipants={canManageParticipants}
          canChangeOwner={canChangeOwner}
          canDelete={canDelete}
          canWrite={isAdmin || clients.find(c => c.id === selectedId)?.manager_id === u?.user_id}
          onClose={() => setSelectedId(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ["estoque-clients"] })}
        />
      )}

      {/* Topbar */}
      <div style={{ height: 52, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", padding: "0 24px", flexShrink: 0, gap: 10 }}>
        <h1 style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", margin: 0 }}>APIs de Estoque</h1>
        <div style={{ flex: 1 }} />
        {isAdmin && (
          <button
            onClick={async () => {
              if (!confirm(`Reprocessar TODOS os ${clients.length} clientes agora? Cada um entra na fila do scraper.`)) return;
              setRedeployAllMsg("Enfileirando...");
              try {
                const r = await api.post<{ enqueued: number }>("/estoque/redeploy-all", {});
                setRedeployAllMsg(`${r.enqueued} clientes na fila`);
              } catch { setRedeployAllMsg("Falhou ao enfileirar"); }
            }}
            disabled={redeployAllMsg === "Enfileirando..."}
            title="Mesmo job do cron: raspa todos os clientes e regrava as URLs das fotos"
            style={{ background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "7px 16px", fontSize: 13, cursor: "pointer" }}
          >
            {redeployAllMsg ?? "Atualizar todos"}
          </button>
        )}
        {isAdmin && (
          <button onClick={() => setFotosPadrao(true)} style={{ background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "7px 16px", fontSize: 13, cursor: "pointer" }}>
            Fotos padrão
          </button>
        )}
        {canCreate && (
          <button onClick={() => setCreating(true)} style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontSize: 13, cursor: "pointer" }}>
            + Novo cliente
          </button>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 20, maxWidth: 860 }}>
          <input style={{ flex: "1 1 220px", padding: "8px 12px", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }} placeholder="Buscar por nome, slug, gestor ou código EST-..." value={search} onChange={e => setSearch(e.target.value)} />
          <select value={managerFilter} onChange={e => setManagerFilter(e.target.value)} style={{ padding: "8px 12px", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", flexShrink: 0 }}>
            <option value="">Todos os gestores</option>
            {managers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          {erroFilter && (
            <button
              onClick={() => navigate({ to: "/estoque", search: {} })}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "7px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer",
                background: "var(--danger-soft)", border: "1px solid var(--danger)", color: "var(--danger)",
                fontWeight: 600, flexShrink: 0,
              }}
            >
              Só com erro <XIcon size={12} />
            </button>
          )}
        </div>

        {isLoading && <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando clientes...</span>}
        {error && <span style={{ fontSize: 13, color: "var(--danger)" }}>Erro ao carregar clientes.</span>}

        {filtered.length > 0 && (
          <div style={{ overflowX: "auto", border: "1px solid var(--line)", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--surface-card)" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Nome</th>
                  <th style={thStyle}>Parser</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Veículos</th>
                  <th style={thStyle}>Sem Categoria</th>
                  <th style={thStyle}>Gestor</th>
                  <th style={thStyle}>Última Atualização</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    style={{ cursor: "pointer" }}
                    onMouseOver={e => (e.currentTarget.style.background = "var(--surface-hover)")}
                    onMouseOut={e => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{c.name}</td>
                    <td style={{ ...tdStyle, color: "var(--ink-muted)" }}>{c.parser_used && c.parser_used !== "unknown" ? c.parser_used : "—"}</td>
                    <td style={tdStyle}><StatusBadge status={c.status} /></td>
                    <td style={tdStyle}>{c.vehicle_count}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: c.sem_categoria > 0 ? "var(--warn)" : "var(--ink-muted)" }}>{c.sem_categoria}</td>
                    <td style={{ ...tdStyle, color: "var(--ink-muted)" }}>
                      {c.manager_name}
                      {c.participant_count ? <span style={{ color: "var(--warn)", fontWeight: 600 }}> +{c.participant_count}</span> : null}
                    </td>
                    <td style={{ ...tdStyle, color: "var(--ink-muted)" }}>{fmtDate(c.last_updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && filtered.length === 0 && clients.length > 0 && (
          <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhum cliente encontrado.</span>
        )}
        {!isLoading && clients.length === 0 && !creating && (
          <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhum cliente ainda. Clique em "+ Novo cliente" para começar.</span>
        )}
      </div>
    </div>
  );
}
